/**
 * authorCascadeDelete — chunked recursive deletion for a full self-author
 * tree (used by Creator Import "replace existing" flow).
 *
 * A self-author owns docs across THREE path levels:
 *   1. projects/{pid}/authors/{aid}                              — root doc
 *   2. .../authors/{aid}/platforms/{platformId}                  — N platforms
 *   3. .../platforms/{pid}/{voice|formats|post_examples|         — 5 nested
 *                            post_templates|author_posts}          subcollections per platform
 * plus 2 author-scoped sibling collections at project level:
 *   - projects/{pid}/topic_pillars/*  (filtered by authorIds.includes(authorId))
 *   - projects/{pid}/creator_references/cref_self_<authorId>     (legacy self-ref)
 *
 * Deletion is chunked at 400 ops/batch (Firestore limit 500 — leaves headroom),
 * matching the precedent in AuthorContentCorpusService.deleteAuthorPosts. Batch
 * deletes are idempotent — calling on a missing doc is a silent no-op.
 *
 * Used by AuthorImportService.replaceAuthor() before writing the imported tree.
 * Caller is responsible for warning users via a confirmation dialog because
 * this operation cannot be undone.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  type CollectionReference,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { ContentStrategyFirestoreService } from './ContentStrategyFirestoreService';

const BATCH_CHUNK_SIZE = 400;

export interface CascadeDeleteCounts {
  authorPosts: number;
  postTemplates: number;
  postExamples: number;
  formats: number;
  voiceDocs: number;
  designDocs: number;
  designProfileSets: number;
  designProfiles: number;
  designReferences: number;
  platforms: number;
  topicPillars: number;
  creatorReferences: number;
  contentStrategies: number;
  topics: number;
  contentPosts: number;
  topicGenerationRuns: number;
  topicGenerationDrafts: number;
  postGenerationRuns: number;
  postGenerationDrafts: number;
  researchRuns: number;
  backendTasks: number;
}

function emptyCascadeDeleteCounts(): CascadeDeleteCounts {
  return {
    authorPosts: 0,
    postTemplates: 0,
    postExamples: 0,
    formats: 0,
    voiceDocs: 0,
    designDocs: 0,
    designProfileSets: 0,
    designProfiles: 0,
    designReferences: 0,
    platforms: 0,
    topicPillars: 0,
    creatorReferences: 0,
    contentStrategies: 0,
    topics: 0,
    contentPosts: 0,
    topicGenerationRuns: 0,
    topicGenerationDrafts: 0,
    postGenerationRuns: 0,
    postGenerationDrafts: 0,
    researchRuns: 0,
    backendTasks: 0,
  };
}

export async function previewCascadeDeleteSelfAuthor(
  projectId: string,
  authorId: string,
): Promise<CascadeDeleteCounts> {
  const db = CoreFirebaseService.getInstance().firestore;
  const counts = emptyCascadeDeleteCounts();

  const platformsSnap = await getDocs(collection(
    db, 'projects', projectId, 'authors', authorId, 'platforms',
  ));
  counts.platforms = platformsSnap.docs.length;

  for (const platformDoc of platformsSnap.docs) {
    const platformId = platformDoc.id;
    const platformPath = ['projects', projectId, 'authors', authorId, 'platforms', platformId] as const;
    counts.authorPosts += (await getDocs(collection(db, ...platformPath, 'author_posts'))).docs.length;
    counts.postTemplates += (await getDocs(collection(db, ...platformPath, 'post_templates'))).docs.length;
    counts.postExamples += (await getDocs(collection(db, ...platformPath, 'post_examples'))).docs.length;
    counts.formats += (await getDocs(collection(db, ...platformPath, 'formats'))).docs.length;
    counts.voiceDocs += (await getDocs(collection(db, ...platformPath, 'voice'))).docs.length;
    counts.designDocs += (await getDocs(collection(db, ...platformPath, 'design'))).docs.length;
    const profileSetsSnap = await getDocs(collection(db, ...platformPath, 'design_profile_sets'));
    counts.designProfileSets += profileSetsSnap.docs.length;
    for (const setDoc of profileSetsSnap.docs) {
      const profilesSnap = await getDocs(collection(setDoc.ref, 'profiles'));
      counts.designProfiles += profilesSnap.docs.length;
      for (const profileDoc of profilesSnap.docs) {
        counts.designReferences += (await getDocs(collection(profileDoc.ref, 'references'))).docs.length;
      }
    }
  }

  counts.topicPillars = (await getDocs(query(
    collection(db, 'projects', projectId, 'topic_pillars'),
    where('authorIds', 'array-contains', authorId),
  ))).docs.length;

  counts.creatorReferences = (await getDoc(doc(
    db, 'projects', projectId, 'creator_references', `cref_self_${authorId}`,
  ))).exists() ? 1 : 0;

  counts.contentStrategies = (await getDocs(query(
    collection(db, 'projects', projectId, 'content_strategies'),
    where('authorId', '==', authorId),
  ))).docs.length;
  counts.topics = (await getDocs(query(
    collection(db, 'projects', projectId, 'topic_backlog'),
    where('authorId', '==', authorId),
  ))).docs.length;
  counts.contentPosts = (await getDocs(query(
    collection(db, 'projects', projectId, 'content_posts'),
    where('authorId', '==', authorId),
  ))).docs.length;

  const topicGenerationsSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'topic_generations'),
    where('authorId', '==', authorId),
  ));
  counts.topicGenerationRuns = topicGenerationsSnap.docs.length;
  for (const runDoc of topicGenerationsSnap.docs) {
    counts.topicGenerationDrafts += (await getDocs(collection(runDoc.ref, 'generatedTopics'))).docs.length;
  }

  const postGenerationsSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'post_generations'),
    where('authorId', '==', authorId),
  ));
  counts.postGenerationRuns = postGenerationsSnap.docs.length;
  for (const runDoc of postGenerationsSnap.docs) {
    counts.postGenerationDrafts += (await getDocs(collection(runDoc.ref, 'generatedPosts'))).docs.length;
  }

  counts.researchRuns = (await getDocs(query(
    collection(db, 'projects', projectId, 'research_runs'),
    where('authorId', '==', authorId),
  ))).docs.length;
  counts.backendTasks = (await getDocs(query(
    collection(db, 'projects', projectId, 'backend_tasks'),
    where('authorId', '==', authorId),
  ))).docs.length;

  return counts;
}

/**
 * Delete every doc that belongs to `authorId` under `projectId`.
 * Returns counts per collection for telemetry / success toast.
 *
 * Order:
 *   1. Per platform: subcollections (heavy → light), then platform doc.
 *   2. Author root doc.
 *   3. topic_pillars filtered by authorIds.
 *   4. creator_references/cref_self_<authorId>.
 *
 * If the author doc doesn't exist, returns zero counts (no-op).
 */
export async function cascadeDeleteSelfAuthor(
  projectId: string,
  authorId: string,
): Promise<CascadeDeleteCounts> {
  const db = CoreFirebaseService.getInstance().firestore;
  const strategyService = new ContentStrategyFirestoreService();

  const counts = emptyCascadeDeleteCounts();

  // 1. Iterate platforms and delete every nested subcollection.
  const platformsCol = collection(
    db, 'projects', projectId, 'authors', authorId, 'platforms',
  );
  const platformsSnap = await getDocs(platformsCol);

  for (const platformDoc of platformsSnap.docs) {
    const platformId = platformDoc.id;
    const platformPath = ['projects', projectId, 'authors', authorId, 'platforms', platformId] as const;

    // author_posts (largest — can be 1000s)
    counts.authorPosts += await deleteCollection(
      collection(db, ...platformPath, 'author_posts'),
    );
    // post_templates
    counts.postTemplates += await deleteCollection(
      collection(db, ...platformPath, 'post_templates'),
    );
    // post_examples (legacy curated list)
    counts.postExamples += await deleteCollection(
      collection(db, ...platformPath, 'post_examples'),
    );
    // formats (copied from system catalog at onboarding)
    counts.formats += await deleteCollection(
      collection(db, ...platformPath, 'formats'),
    );
    // voice (single-doc subcollection — typically 1 doc with id 'voice')
    counts.voiceDocs += await deleteCollection(
      collection(db, ...platformPath, 'voice'),
    );
    counts.designDocs += await deleteCollection(
      collection(db, ...platformPath, 'design'),
    );
    const profileSetsSnap = await getDocs(collection(db, ...platformPath, 'design_profile_sets'));
    for (const setDoc of profileSetsSnap.docs) {
      const profilesSnap = await getDocs(collection(setDoc.ref, 'profiles'));
      for (const profileDoc of profilesSnap.docs) {
        counts.designReferences += await deleteCollection(collection(profileDoc.ref, 'references'));
      }
      counts.designProfiles += await deleteDocs(profilesSnap.docs.map((d) => d.ref));
    }
    counts.designProfileSets += await deleteDocs(profileSetsSnap.docs.map((d) => d.ref));

    // The platform doc itself.
    const platformBatch = writeBatch(db);
    platformBatch.delete(platformDoc.ref);
    await platformBatch.commit();
    counts.platforms += 1;
  }

  // 2. Author root doc.
  const authorRef = doc(db, 'projects', projectId, 'authors', authorId);
  const authorBatch = writeBatch(db);
  authorBatch.delete(authorRef);
  await authorBatch.commit();

  // 3. topic_pillars filtered by authorIds.array-contains.
  const pillarsCol = collection(db, 'projects', projectId, 'topic_pillars');
  const pillarsQuery = query(pillarsCol, where('authorIds', 'array-contains', authorId));
  const pillarsSnap = await getDocs(pillarsQuery);
  if (!pillarsSnap.empty) {
    counts.topicPillars = await deleteDocs(pillarsSnap.docs.map((d) => d.ref));
  }

  // 4. Self-reference creator_references doc (deterministic id).
  const crefRef = doc(
    db, 'projects', projectId, 'creator_references', `cref_self_${authorId}`,
  );
  const crefBatch = writeBatch(db);
  crefBatch.delete(crefRef);
  await crefBatch.commit();
  counts.creatorReferences = 1; // Best-effort — Firestore silently no-ops missing docs.

  // 5. Author-owned content-plan workspace docs.
  const strategiesSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'content_strategies'),
    where('authorId', '==', authorId),
  ));
  for (const strategyDoc of strategiesSnap.docs) {
    await strategyService.deleteStrategyWithArtifacts(projectId, strategyDoc.id);
    counts.contentStrategies += 1;
  }

  const topicsSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'topic_backlog'),
    where('authorId', '==', authorId),
  ));
  counts.topics = await deleteDocs(topicsSnap.docs.map((d) => d.ref));

  const contentPostsSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'content_posts'),
    where('authorId', '==', authorId),
  ));
  counts.contentPosts += await deleteDocs(contentPostsSnap.docs.map((d) => d.ref));

  const topicGenerationsSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'topic_generations'),
    where('authorId', '==', authorId),
  ));
  for (const runDoc of topicGenerationsSnap.docs) {
    const drafts = await getDocs(collection(runDoc.ref, 'generatedTopics'));
    counts.topicGenerationDrafts += await deleteDocs(drafts.docs.map((d) => d.ref));
  }
  counts.topicGenerationRuns = await deleteDocs(topicGenerationsSnap.docs.map((d) => d.ref));

  const postGenerationsSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'post_generations'),
    where('authorId', '==', authorId),
  ));
  for (const runDoc of postGenerationsSnap.docs) {
    const drafts = await getDocs(collection(runDoc.ref, 'generatedPosts'));
    counts.postGenerationDrafts += await deleteDocs(drafts.docs.map((d) => d.ref));
  }
  counts.postGenerationRuns += await deleteDocs(postGenerationsSnap.docs.map((d) => d.ref));

  const researchRunsSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'research_runs'),
    where('authorId', '==', authorId),
  ));
  counts.researchRuns = await deleteDocs(researchRunsSnap.docs.map((d) => d.ref));

  const taskSnap = await getDocs(query(
    collection(db, 'projects', projectId, 'backend_tasks'),
    where('authorId', '==', authorId),
  ));
  counts.backendTasks = await deleteDocs(taskSnap.docs.map((d) => d.ref));

  return counts;
}

/**
 * Delete every doc in `colRef`, chunked at 400 ops/batch. Returns count.
 * Ignores errors from individual missing docs (Firestore batch deletes are
 * idempotent).
 */
async function deleteCollection(colRef: CollectionReference): Promise<number> {
  const snap = await getDocs(colRef);
  if (snap.empty) return 0;
  return deleteDocs(snap.docs.map((d) => d.ref));
}

/**
 * Delete a list of doc refs, chunked at 400 ops/batch. Returns count.
 */
async function deleteDocs(refs: ReadonlyArray<ReturnType<typeof doc>>): Promise<number> {
  const db = CoreFirebaseService.getInstance().firestore;
  for (let i = 0; i < refs.length; i += BATCH_CHUNK_SIZE) {
    const chunk = refs.slice(i, i + BATCH_CHUNK_SIZE);
    const batch = writeBatch(db);
    for (const ref of chunk) batch.delete(ref);
    await batch.commit();
  }
  return refs.length;
}
